export function computeDualAxisWaveform(input) {
  const samples = Math.min(Math.max(input.samples ?? 360, 64), 2048)
  const durationSec = input.durationSec ?? 1
  const omega = 2 * Math.PI * input.frequencyHz
  const phi = (input.phaseShiftDeg * Math.PI) / 180
  const F0 = input.axialLoadKn
  const M0 = input.torsionTorqueNm
  const t = []
  const axial = []
  const torsion = []
  const lissajous = []
  for (let i = 0; i < samples; i++) {
    const ti = (i / samples) * durationSec
    const fz = F0 * Math.sin(omega * ti)
    const mz = M0 * Math.sin(omega * ti + phi)
    t.push(ti)
    axial.push(Number(fz.toFixed(4)))
    torsion.push(Number(mz.toFixed(4)))
    lissajous.push({ x: Number(fz.toFixed(4)), y: Number(mz.toFixed(4)) })
  }
  const phaseCoupling = Math.abs(Math.sin(phi))
  const thermalFactor = 1 + Math.max(0, input.tempCelsius - 20) / 1200
  const vonMisesProxy = Math.sqrt(F0 * F0 + 3 * (M0 / 100) * (M0 / 100)) * thermalFactor
  const damageIndex = Number((vonMisesProxy * (0.55 + 0.45 * phaseCoupling) * Math.sqrt(input.frequencyHz)).toFixed(3))
  return {
    equations: {
      axial: 'Fz = F0 · sin(ωt)',
      torsion: 'Mz = M0 · sin(ωt + φ)',
      omega: `ω = 2πf = ${omega.toFixed(4)} rad/s`,
      phiRad: Number(phi.toFixed(6)),
    },
    metrics: {
      vonMisesProxy: Number(vonMisesProxy.toFixed(3)),
      damageIndex,
      phaseCoupling: Number(phaseCoupling.toFixed(4)),
      thermalFactor: Number(thermalFactor.toFixed(4)),
      ellipseEccentricity: Number((1 - Math.min(F0, M0 / 10) / Math.max(F0, M0 / 10, 1e-6)).toFixed(4)),
    },
    series: { t, axial, torsion, lissajous },
  }
}

export function evaluateQualification(input) {
  const axes = {
    manufacturerAuth: input.hasManufacturerAuth ? 100 : 0,
    bankCredit: input.hasBankCreditCert
      ? Math.min(100, 60 + (input.bankCertMonths && input.bankCertMonths >= 3 ? 40 : 0))
      : 0,
    foreignTrade: input.bidderType === 'OVERSEAS'
      ? (input.hasForeignTradeContractCapacity ? 100 : 0)
      : 85,
    creditIntegrity: input.hasCreditChinaViolation ? 0 : 100,
    documentCompleteness: [
      input.hasManufacturerAuth,
      input.hasBankCreditCert,
      input.bidderType === 'DOMESTIC' || input.hasForeignTradeContractCapacity,
      !input.hasCreditChinaViolation,
    ].filter(Boolean).length * 25,
  }
  let score =
    axes.manufacturerAuth * 0.25 +
    axes.bankCredit * 0.25 +
    axes.foreignTrade * 0.2 +
    axes.creditIntegrity * 0.2 +
    axes.documentCompleteness * 0.1
  const risks = []
  if (!input.hasManufacturerAuth) risks.push('缺少制造商授权 / 原装同源证明')
  if (!input.hasBankCreditCert) risks.push('缺少近3个月银行资信证明')
  if (input.bidderType === 'OVERSEAS' && !input.hasForeignTradeContractCapacity) {
    risks.push('关境外主体不具备外贸合同签署与合规收汇能力')
  }
  if (input.hasCreditChinaViolation) risks.push('命中失信被执行人 / 信用中国风险标记')
  if (input.hasCreditChinaViolation) score = Math.min(score, 35)
  const verdict = score >= 85 ? 'PASS' : score >= 60 ? 'CONDITIONAL' : 'FAIL'
  return {
    complianceScore: Number(score.toFixed(2)),
    verdict,
    axes,
    risks,
    radar: [
      { axis: '制造商授权', value: axes.manufacturerAuth },
      { axis: '银行资信', value: axes.bankCredit },
      { axis: '外贸履约', value: axes.foreignTrade },
      { axis: '信用完整性', value: axes.creditIntegrity },
      { axis: '要件完备度', value: axes.documentCompleteness },
    ],
  }
}
